import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getNavigationData, getCachedItemById } from './navigationCache';
import {
    FormControl,
    Stack,
    FormLabel,
    Box,
    Text,
    VStack,
    HStack,
    Input,
    InputGroup,
    InputRightElement,
    IconButton,
    Tooltip,
} from '@chakra-ui/react';
import { CloseIcon, SearchIcon } from '@chakra-ui/icons';
import {
    stackSpacing,
    formLabelStyling,
    stackStyling,
    descriptionStyling,
} from 'components/forms/new/formStyles';
import FieldToolTip from 'components/forms/new/FieldToolTip';
import PartnerCompanyPicker from '@/components/pickers/PartnerCompanyPicker';

/**
 * Reusable Partner/Company Field Component
 * Uses the PartnerCompanyPicker modal for selection
 *
 * @param {object} values - Form values object
 * @param {function} setFieldValue - Formik setFieldValue function
 * @param {object} errors - Formik errors object
 * @param {object} touched - Formik touched object
 * @param {boolean} darkMode - Dark mode flag
 * @param {string} mode - 'partner' or 'company' - determines behavior
 * @param {string} label - Field label
 * @param {string} description - Field description
 * @param {boolean} required - Whether field is required
 */
const PartnerCompanyField = ({
    values,
    setFieldValue,
    errors,
    touched,
    darkMode,
    mode = 'company', // 'partner' or 'company'
    label = 'Partner / Company',
    description = 'Select the partner and/or company.',
    required = false,
    partnerFieldName = 'partnerID',
    companyFieldName = 'companyID',
    partnerNameField = 'partner', // Field name for partner name from backend
    companyNameField = 'company', // Field name for company name from backend
    wholesalerFieldName = 'wholesalerID', // New prop for wholesaler field name
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [cacheLoaded, setCacheLoaded] = useState(0); // Counter to trigger re-render when cache loads

    const { currentPartner, requiredScope, currentCompany } =
        useSelector((state) => ({
            currentPartner: state.navigationLists.currentPartner,
            currentCompany: state.navigationLists.currentCompany,
            requiredScope: state.login.requiredScope,
        }));

    // Use form's wholesaler ONLY - don't fall back to Redux to avoid stale data
    const activeWholesaler = values[wholesalerFieldName] || null;
    // Use requiredScope from Redux for API calls - normalize 80 to 60
    const rawScope = requiredScope || 60;
    const activeScope = rawScope === 40 ? 40 : 60; // Only 40 or 60, never 80
    // For scope 40, use partner from form or Redux
    const activePartnerId = values[partnerFieldName] || currentPartner;

    // Extract values for dependencies
    const partnerIdValue = values[partnerFieldName];
    const companyIdValue = values[companyFieldName];
    const partnerNameValue = values[partnerNameField];
    const companyNameValue = values[companyNameField];
    const companyIsTrialValue = values.companyIsTrial;

    // Compute display values - prioritize backend name fields, fall back to cache lookup
    const { partnerName, companyName } = useMemo(() => {
        let partner = '';
        let company = '';
        let domain = '';
        let tenantID = '';
        let isTrial = '';

        // First, check if backend provided the names directly (e.g., from edit form load)
        if (partnerNameValue) {
            partner = partnerNameValue;
        }
        if (companyNameValue) {
            company = companyNameValue;
        }

        // If no backend names, try cache lookup (for modal selections)
        if (!partner && activeWholesaler && partnerIdValue) {
            const partnerItem = getCachedItemById(
                activeWholesaler,
                partnerIdValue,
                activeScope,
                activePartnerId,
            );
            if (
                partnerItem &&
                (!partnerItem.parent || partnerItem.parent.length === 0)
            ) {
                partner = partnerItem.friendlyName || partnerItem.name;
            }
        }
        if (!company && activeWholesaler && companyIdValue) {
            const companyItem = getCachedItemById(
                activeWholesaler,
                companyIdValue,
                activeScope,
                activePartnerId,
            );
            if (companyItem) {
                company = companyItem.friendlyName || companyItem.name;
                domain = companyItem.initialMicrosoftDomain || '';
                tenantID = companyItem.tenantID || '';
            }
        }
        // Check if companyIsTrial is explicitly set in form values (from picker selection)

        if (companyIsTrialValue !== undefined) {
            isTrial = companyIsTrialValue;
        }

        return {
            partnerName: partner,
            companyName: company,
            companyDomain: domain,
            companyTenantID: tenantID,
            companyIsTrial: isTrial,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        activeWholesaler,
        activeScope,
        activePartnerId,
        partnerIdValue,
        companyIdValue,
        partnerNameValue,
        companyNameValue,
        cacheLoaded,
    ]);

    // Aggressive prefetch: Load data immediately on mount and when context changes
    // This ensures modal can open instantly
    useEffect(() => {
        // For scope 40 need partnerId, for scope 60+ need wholesalerId
        if (
            (activeScope === 40 && activePartnerId) ||
            (activeScope >= 60 && activeWholesaler)
        ) {
            getNavigationData(activeWholesaler, activeScope, activePartnerId)
                .then(() => {
                    setCacheLoaded((prev) => prev + 1);
                })
                .catch(() => {});
        }
    }, [activeWholesaler, activeScope, activePartnerId]); // Refetch when context changes

    const handleSelect = (selection) => {
        // Update IDs - names will be computed from cache via useMemo
        if (selection.partnerId !== undefined) {
            setFieldValue(partnerFieldName, selection.partnerId || '');
            // Clear backend name field so useMemo will use cache value
            setFieldValue(partnerNameField, '');
        }
        if (selection.companyId !== undefined) {
            setFieldValue(companyFieldName, selection.companyId || '');
            // Clear backend name field so useMemo will use cache value
            setFieldValue(companyNameField, '');

            // Set trial status for block size filtering
            setFieldValue('companyIsTrial', selection.companyIsTrial ?? null);
        }
    };

    const handleClear = () => {
        if (mode === 'partner') {
            setFieldValue(partnerFieldName, '');
            setFieldValue(companyFieldName, '');
            // Also clear the name fields so display updates
            setFieldValue(partnerNameField, '');
            setFieldValue(companyNameField, '');
            setFieldValue('companyIsTrial', null);
        } else {
            setFieldValue(companyFieldName, '');
            // Also clear the name field so display updates
            setFieldValue(companyNameField, '');
            setFieldValue('companyIsTrial', null);
        }
    };

    const getDisplayValue = () => {
        if (mode === 'company') {
            // Show company name if loaded
            if (companyName) return companyName;
            // Show empty if no company
            if (!values[companyFieldName]) return '';
            // Show ID as fallback (better than Loading...)
            return values[companyFieldName];
        } else {
            // Partner mode - show partner name
            if (partnerName) return partnerName;
            // Show empty if no partner ID
            if (!values[partnerFieldName]) return '';
            // Show ID as fallback (better than Loading...)
            return values[partnerFieldName];
        }
    };

    const displayValue = getDisplayValue();
    const hasValue = displayValue !== '';
    const hasError =
        (mode === 'company' &&
            errors[companyFieldName] &&
            touched[companyFieldName]) ||
        (mode === 'partner' &&
            errors[partnerFieldName] &&
            touched[partnerFieldName]);

    // Disable picker if no wholesaler is selected
    const isDisabled = !activeWholesaler;

    // Always show both fields
    return (
        <>
            <FormControl
                sx={{ margin: '0 !important' }}
                isInvalid={hasError}
                isRequired={required}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}>
                <Stack
                    direction="row"
                    spacing={stackSpacing}
                    sx={stackStyling(true)}>
                    <Box>
                        <FormLabel sx={formLabelStyling(darkMode)}>
                            {label}
                            {required && (
                                <Text as="span" color="red.500">
                                    {' '}
                                    *
                                </Text>
                            )}
                        </FormLabel>
                        <Text sx={descriptionStyling} maxWidth={250}>
                            {description}
                        </Text>
                    </Box>
                    <Box flex="1" minWidth="450px">
                        <Box
                            sx={{
                                display: 'flex',
                                width: '100%',
                                alignItems: 'flex-start',
                                paddingTop: '15px',
                            }}>
                            <VStack
                                width="100%"
                                alignItems="normal"
                                spacing={2}>
                                <InputGroup>
                                    <Tooltip
                                        label={
                                            isDisabled
                                                ? 'Please select a wholesaler first'
                                                : displayValue
                                        }
                                        isDisabled={
                                            !isDisabled &&
                                            (!displayValue ||
                                                displayValue.length < 30)
                                        }
                                        placement="top"
                                        hasArrow
                                        openDelay={500}>
                                        <Input
                                            value={displayValue}
                                            placeholder="Not Assigned"
                                            readOnly
                                            cursor={
                                                isDisabled
                                                    ? 'not-allowed'
                                                    : 'pointer'
                                            }
                                            isDisabled={isDisabled}
                                            onClick={() =>
                                                !isDisabled &&
                                                setIsPickerOpen(true)
                                            }
                                            style={{
                                                width: '100%',
                                                height: '40px',
                                                padding: '8px 14px',
                                                paddingRight: '120px',
                                                fontSize: '16px',
                                                fontFamily:
                                                    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                                background: 'transparent',
                                                color: darkMode
                                                    ? 'rgba(255, 255, 255, 0.87)'
                                                    : 'rgba(0, 0, 0, 0.87)',
                                                border: darkMode
                                                    ? '1px solid rgba(255, 255, 255, 0.23)'
                                                    : '1px solid rgba(0, 0, 0, 0.23)',
                                                borderRadius: '4px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        />
                                    </Tooltip>
                                    <InputRightElement width="auto" pr={2}>
                                        <HStack spacing={1}>
                                            {hasValue && !isDisabled && (
                                                <IconButton
                                                    size="sm"
                                                    variant="ghost"
                                                    icon={<CloseIcon />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleClear();
                                                    }}
                                                    aria-label="Clear selection"
                                                />
                                            )}
                                            <IconButton
                                                size="sm"
                                                variant="ghost"
                                                icon={<SearchIcon />}
                                                onClick={() =>
                                                    !isDisabled &&
                                                    setIsPickerOpen(true)
                                                }
                                                isDisabled={isDisabled}
                                                aria-label="Open picker"
                                            />
                                        </HStack>
                                    </InputRightElement>
                                </InputGroup>

                                {/* Show domain and tenantID for company mode */}
                                {/* {mode === 'company' && (companyDomain || companyTenantID) && (
                                    <VStack align="start" spacing={0} fontSize="xs" color={darkMode ? 'gray.400' : 'gray.600'} mt={1}>
                                        {companyDomain && (
                                            <Text>Domain: {companyDomain}</Text>
                                        )}
                                        {companyTenantID && (
                                            <Text>TenantID: {companyTenantID}</Text>
                                        )}
                                    </VStack>
                                )} */}
                            </VStack>
                        </Box>
                    </Box>
                    {isHovered && (
                        <FieldToolTip
                            label={description}
                            ariaLabel="partner-company-tip"
                            left="25px"
                            top="25px"
                        />
                    )}
                </Stack>
            </FormControl>

            {/* Partner/Company Picker Modal - Only mount when needed! */}
            {isPickerOpen && (
                <PartnerCompanyPicker
                    isOpen={isPickerOpen}
                    onClose={() => {
                        setIsPickerOpen(false);
                    }}
                    onSelect={handleSelect}
                    currentPartnerId={values[partnerFieldName]}
                    currentCompanyId={values[companyFieldName]}
                    mode={mode}
                    wholesalerId={activeWholesaler}
                    scope={activeScope}
                    managedCompanyId={currentCompany}
                    managedPartnerId={currentPartner}
                />
            )}
        </>
    );
};

export default PartnerCompanyField;
